import React from 'react';
import { cn } from '@/utils/classNames';

/** アイコンサイズの型定義 */
type IconSize = 'sm' | 'md' | 'lg';

interface PageTitleProps {
  /**
   * Heroiconsなどのアイコンコンポーネント
   * @example UserGroupIcon, DocumentTextIcon
   * @default undefined（表示しない）
   */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * ページタイトルテキスト
   * @example "取引先管理", "案件一覧"
   */
  title: string;
  /**
   * ページサブタイトルテキスト
   * @example "取引先企業の管理", "配車案件一覧"
   * @default undefined（表示しない）
   */
  subtitle?: string;
  /**
   * 追加のCSSクラス（Tailwind CSS）
   * @default ""
   */
  className?: string;
  /**
   * アイコンのサイズ設定
   * - sm: 小サイズ（h-5 w-5 sm:h-6 sm:w-6）
   * - md: 中サイズ（h-6 w-6 sm:h-8 sm:w-8）- デフォルト
   * - lg: 大サイズ（h-8 w-8 sm:h-10 sm:w-10）
   * @default "md"
   */
  iconSize?: IconSize;
}

const iconSizeClasses = {
  sm: 'h-5 w-5 sm:h-6 sm:w-6',
  md: 'h-6 w-6 sm:h-8 sm:w-8',
  lg: 'h-8 w-8 sm:h-10 sm:w-10',
} as const as Record<IconSize, string>;

/**
 * ページタイトルコンポーネント
 *
 * 全ページで統一されたタイトル表示を提供します。
 * ダークモード完全対応、レスポンシブデザイン、アクセシビリティを考慮。
 *
 * @example
 * ```tsx
 * import { DocumentTextIcon } from '@heroicons/react/24/outline';
 * import { PageTitle } from '@/components/ui';
 *
 * // icon あり
 * <PageTitle
 *   icon={DocumentTextIcon}
 *   title="案件管理"
 * />
 *
 * // icon なし、subtitle あり
 * <PageTitle
 *   title="配車案件"
 *   subtitle="陸送員向け配車案件一覧"
 * />
 * ```
 */
export const PageTitle: React.FC<PageTitleProps> = ({
  icon: Icon,
  title,
  subtitle,
  className = '',
  iconSize = 'md',
}) => {
  return (
    <div className={className}>
      <h1
        className={cn(
          // ベーススタイル - 動作しているページのパターンを採用
          'text-2xl sm:text-3xl font-bold flex items-center',
          // 強制的なダークモード対応
          'text-gray-800 dark:text-white',
          // subtitle がある場合は余白調整
          subtitle ? 'mb-2' : 'mb-4 sm:mb-6'
        )}
      >
        {Icon && (
          <Icon
            className={cn(iconSizeClasses[iconSize], 'mr-3', 'text-gray-700 dark:text-gray-300')}
            aria-hidden='true'
          />
        )}
        <span>{title}</span>
      </h1>
      {subtitle && (
        <p className='text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6'>
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default PageTitle;
